import { useAuth } from '@clerk/clerk-react'
import { useState, type FormEvent } from 'react'

import { Button } from '@/components/ui/Button'
import { FIELD, LABEL } from '@/components/ui/formFields'
import { GlassCard } from '@/components/ui/GlassCard'
import { ApiError } from '@/lib/api/client'
import { submitFeedback } from '@/lib/api/feedback'
import { cn } from '@/lib/cn'
import type { FeedbackTopic } from '@/lib/types/feedback'

const TOPICS: { label: string; value: FeedbackTopic }[] = [
  { label: 'Bug', value: 'bug' },
  { label: 'Idea', value: 'idea' },
  { label: 'Other', value: 'other' },
]

const MESSAGE_MIN = 10
const MESSAGE_MAX = 2000

function looksLikeEmail(value: string): boolean {
  const email = value.trim().toLowerCase()
  if (!email.includes('@') || email.startsWith('@') || email.endsWith('@')) {
    return false
  }
  const [local, domain] = email.split('@')
  return Boolean(local && domain?.includes('.'))
}

function pagePath(): string {
  try {
    const ref = document.referrer
    if (ref) {
      const url = new URL(ref)
      if (
        url.origin === window.location.origin &&
        url.pathname !== '/support'
      ) {
        return `${url.pathname}${url.search}`
      }
    }
  } catch {
    // ignore bad referrer
  }
  return window.location.pathname
}

function submitError(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 429) {
      return "That's enough for now. You can send more in an hour."
    }
    return err.detail || "Couldn't send that."
  }
  return "Couldn't send that."
}

export function FeedbackForm() {
  const { isLoaded, isSignedIn, getToken } = useAuth()
  const [topic, setTopic] = useState<FeedbackTopic | null>(null)
  const [message, setMessage] = useState('')
  const [email, setEmail] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [capped, setCapped] = useState(false)
  const [sent, setSent] = useState(false)

  const needsEmail = isLoaded && !isSignedIn
  const blocked = submitting || !isLoaded

  function reset() {
    setTopic(null)
    setMessage('')
    setEmail('')
    setError(null)
    setCapped(false)
    setSent(false)
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setError(null)

    if (!topic) {
      setError('Pick a topic.')
      return
    }
    const body = message.trim()
    if (body.length < MESSAGE_MIN) {
      setError(`Tell us a bit more (at least ${MESSAGE_MIN} characters).`)
      return
    }
    if (body.length > MESSAGE_MAX) {
      setError(`Keep it under ${MESSAGE_MAX} characters.`)
      return
    }
    if (needsEmail && !looksLikeEmail(email)) {
      setError('Enter a valid email.')
      return
    }

    setSubmitting(true)
    try {
      await submitFeedback(
        {
          topic,
          message: body,
          email: needsEmail ? email.trim() : undefined,
          path: pagePath(),
        },
        isSignedIn ? getToken : undefined,
      )
      setSent(true)
      setTopic(null)
      setMessage('')
      setEmail('')
      setCapped(false)
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) {
        setCapped(true)
      }
      setError(submitError(err))
    } finally {
      setSubmitting(false)
    }
  }

  if (sent) {
    return (
      <GlassCard className="space-y-4 p-6 md:p-8">
        <p className="font-body text-body-md text-fg" role="status">
          Got it. We&apos;ll read this.
        </p>
        <Button type="button" variant="ghost" size="md" onClick={reset}>
          Send another
        </Button>
      </GlassCard>
    )
  }

  return (
    <GlassCard className="p-6 md:p-8">
      <form
        onSubmit={(event) => void handleSubmit(event)}
        className="space-y-6"
      >
        {capped && (
          <div
            role="status"
            className="border-primary-container/30 bg-primary-container/15 rounded-lg border px-4 py-3"
          >
            <p className="font-body text-body-md text-fg font-semibold">
              That&apos;s enough for now.
            </p>
            <p className="font-body text-body-sm text-fg mt-1">
              You can send more in an hour.
            </p>
          </div>
        )}

        <fieldset className="space-y-3" disabled={blocked}>
          <legend className={LABEL}>Topic</legend>
          <div role="group" aria-label="Topic" className="flex flex-wrap gap-2">
            {TOPICS.map((option) => {
              const selected = topic === option.value
              return (
                <button
                  key={option.value}
                  type="button"
                  aria-pressed={selected}
                  onClick={() => {
                    setTopic(option.value)
                  }}
                  className={cn(
                    'font-body text-label-caps min-h-11 rounded-full border px-4 uppercase transition-colors',
                    selected
                      ? 'border-primary-container/30 bg-primary-container/15 text-primary'
                      : 'border-glass-border text-fg-muted hover:text-fg bg-white/5',
                  )}
                >
                  {option.label}
                </button>
              )
            })}
          </div>
        </fieldset>

        {needsEmail && (
          <div className="space-y-2">
            <label htmlFor="feedback-email" className={LABEL}>
              Email
            </label>
            <input
              id="feedback-email"
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(event) => {
                setEmail(event.target.value)
              }}
              disabled={blocked}
              className={FIELD}
            />
          </div>
        )}

        <div className="space-y-2">
          <label htmlFor="feedback-message" className={LABEL}>
            Message
          </label>
          <textarea
            id="feedback-message"
            required
            minLength={MESSAGE_MIN}
            maxLength={MESSAGE_MAX}
            rows={6}
            value={message}
            onChange={(event) => {
              setMessage(event.target.value)
            }}
            placeholder="What should we know?"
            disabled={blocked}
            className={cn(FIELD, 'min-h-36 resize-y')}
          />
          <p className="font-body text-label-sm text-fg-muted">
            {message.trim().length} / {MESSAGE_MAX}
          </p>
        </div>

        {error && !capped && (
          <p className="font-body text-body-sm text-error" role="alert">
            {error}
          </p>
        )}

        <Button
          type="submit"
          variant="gradient"
          size="lg"
          disabled={blocked || capped}
          className="w-full sm:w-auto"
        >
          {submitting ? 'Sending…' : 'Send'}
        </Button>
      </form>
    </GlassCard>
  )
}
